import CremationAuthorizationForm from '@/components/documents/Foxit/FormMapping/CremationAuthorizationForm'
import DisclosureForm from '@/components/documents/Foxit/FormMapping/DisclosureForm'
import EnrollmentForm from '@/components/documents/Foxit/FormMapping/EnrollmentForm'
import EverplansForm from '@/components/documents/Foxit/FormMapping/EverplansForm'
import GoodsAndServicesForm from '@/components/documents/Foxit/FormMapping/GoodsAndServicesForm'
import IrrevocableAssignmentForm from '@/components/documents/Foxit/FormMapping/IrrevocableAssignmentForm'
import NGLPaymentAuthorizationForm from '@/components/documents/Foxit/FormMapping/NGL/NGLPaymentAuthorizationForm'
import NGLNCCremationAuthorizationForm from '@/components/documents/Foxit/FormMapping/NGL/NorthCarolina/NGLNCCremationAuthorizationForm'
import NGLNCEnrollmentForm from '@/components/documents/Foxit/FormMapping/NGL/NorthCarolina/NGLNCEnrollmentForm'
import NGLNCGoodsAndServicesForm from '@/components/documents/Foxit/FormMapping/NGL/NorthCarolina/NGLNCGoodsAndServicesForm'
import NGLNCPreneedContractForm from '@/components/documents/Foxit/FormMapping/NGL/NorthCarolina/NGLNCPreneedContractForm'
import NGLUTDisclosureForm from '@/components/documents/Foxit/FormMapping/NGL/Utah/NGLUTDisclosureForm'
import NGLUTEnrollmentForm from '@/components/documents/Foxit/FormMapping/NGL/Utah/NGLUTEnrollmentForm'
import NGLUTGoodsAndServicesForm from '@/components/documents/Foxit/FormMapping/NGL/Utah/NGLUTGoodsAndServicesForm'
import NGLWVGoodsAndServicesForm from '@/components/documents/Foxit/FormMapping/NGL/WestVirginia/NGLWVGoodsAndServicesForm'
import NGLWIDisclosureStatement from '@/components/documents/Foxit/FormMapping/NGL/Wisconsin/NGLWIDisclosureStatement'
import NGLWIEnrollmentForm from '@/components/documents/Foxit/FormMapping/NGL/Wisconsin/NGLWIEnrollmentForm'
import NGLWIGoodsAndServicesForm from '@/components/documents/Foxit/FormMapping/NGL/Wisconsin/NGLWIGoodsAndServicesForm'
import OAP from '@/components/documents/Foxit/FormMapping/Oap'
import PolicyReplacementForm from '@/components/documents/Foxit/FormMapping/PolicyReplacementForm'
import PreneedAgreementForm from '@/components/documents/Foxit/FormMapping/PreneedAgreementForm'
import PreneedContractForm from '@/components/documents/Foxit/FormMapping/PreneedContractForm'
import ReplacementInformationForm from '@/components/documents/Foxit/FormMapping/ReplacementInformationForm'
import RevocableAssignmentForm from '@/components/documents/Foxit/FormMapping/RevocableAssignmentForm'
import SecondaryAddresseeForm from '@/components/documents/Foxit/FormMapping/SecondaryAddresseeForm'
import SummaryOfCoverageForm from '@/components/documents/Foxit/FormMapping/SummaryOfCoverageForm'
import TennesseeGoodsAndServicesForm from '@/components/documents/Foxit/FormMapping/TennesseeGoodsAndServicesForm'
import TexasGoodsAndServicesForm from '@/components/documents/Foxit/FormMapping/TexasGoodsAndServicesForm'
import { CorporateAffiliation } from '@/enums/CorporateAffiliation'
import { DocumentType } from '@/enums/DocumentType'

export enum RoleType {
	default = 'default',
	state = 'state',
	funeralHomeCorporateAffiliation = 'funeralHomeCorporateAffiliation',
}

export type SingleFile = {
	name: string
	link: string
	supplemental?: boolean
	formClass?: (new (...args: any[]) => BaseForm) | null
	alternateFormClass?: (new (...args: any[]) => BaseForm) | null
}

export type FuneralHome = {
	id: string
	name: string
	state: string
	corporateAffiliation?: string
}

export interface BaseForm {
	getFormMappings(): Array<{ name: string; value: string | number | undefined }>
}

// A document can be either a simple file or a conditional object with multiple files
export type ConditionalFile = {
	options: Record<string, SingleFile>
	formClass?: (new (...args: any[]) => BaseForm) | null
	alternateFormClass?: (new (...args: any[]) => BaseForm) | null
}
export type DocumentFile = SingleFile | ConditionalFile

export type DefaultDocumentRule = Partial<Record<DocumentType, DocumentFile>>
export type StateDocumentRule = Record<string, DefaultDocumentRule>

export type ValidState = keyof (typeof DocumentRules)[RoleType.state]

// Funeral home rules are 2-level deep:
// corporateAffiliation (e.g. "Carriage") -> stateCode (e.g. "OH") -> documentType -> file
export type FuneralHomeStateRules = Partial<Record<string, DefaultDocumentRule>>
export type FuneralHomeDocumentRule = Record<string, FuneralHomeStateRules>

export type DocumentRulesByRole = {
	[RoleType.default]: DefaultDocumentRule
	[RoleType.state]: StateDocumentRule
	[RoleType.funeralHomeCorporateAffiliation]: FuneralHomeDocumentRule
}

export const DocumentRules: DocumentRulesByRole = {
	[RoleType.default]: {
		[DocumentType.StatementOfGoodsAndServices]: {
			link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000GS%2006-18%20Default%20%2810.8.25%29.pdf',
			name: '3000G&S 06-18',
			formClass: GoodsAndServicesForm,
		},
		[DocumentType.EnrollmentForm]: {
			link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29%2006-11%20%2810.3.25%29.pdf',
			name: '3000PN(v9) 06-11',
			formClass: EnrollmentForm,
		},
		[DocumentType.PaymentAuthorization]: {
			options: {
				[DocumentType.SinglePaymentAuthorization]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Payment%20Authorization/3000PN-PWA-SPCC%2006-25%20%289%3A18%3A25%29.pdf',
					name: '3000PN-PWA-SPCC 06-25',
				},
				[DocumentType.PaymentAuthorization]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Payment%20Authorization/3000PN-PWA%2006-25%20%289%3A18%3A25%29.pdf',
					name: '3000PN-PWA 06-25',
				},
			},
			formClass: NGLPaymentAuthorizationForm,
		},
		[DocumentType.OAPEnrollment]: {
			options: {
				'Sepio Guard': {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/Sepio/Transport_Guarantee_Service_Agreement_2025_Precoa_Update.pdf',
					name: 'Sepio Enrollment',
				},
				Inman: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/Sepio/Inman%20Service%20Agreement%20Sales%20Studio%202.0.pdf',
					name: 'Inman Enrollment',
				},
			},
			formClass: OAP,
		},
		[DocumentType.EverplansEnrollment]: {
			link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Everplans_Lifetime_Authorization_Form_ReportLab_10.2.25v1.pdf',
			name: 'Everplans Enrollment',
			formClass: EverplansForm,
		},
		[DocumentType.CancellationPolicy]: {
			link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN-NOC%2001-23.pdf',
			name: '3000PN-NOC 01-23',
			formClass: null,
		},
	},
	[RoleType.state]: {
		AL: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-AL%2006-18%20%2810.08.2025%29.pdf',
				name: '3000G&S-AL 06-18',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.DisclosureStatement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/DISCL-PN-AL%2012-21%20%2810.08.2025%29.pdf',
				name: 'DISCL-PN-AL 12/21',
				supplemental: true,
				formClass: DisclosureForm,
			},
		},
		AR: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-AR%2011-21%20%2810.9.25%29.pdf',
				name: '3000G&S-AR 11-21',
				formClass: GoodsAndServicesForm,
			},
		},
		AZ: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-AZ%2006-18%20%289%3A17%3A25%29.pdf',
				name: '3000G&S-AZ 06-18',
				formClass: GoodsAndServicesForm,
			},
		},
		CA: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-CA%2001-24%20%289-17-25%29.pdf',
				name: '3000G&S-CA 01-24',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN-CA%2001-23%20%2810.3.25%29.pdf',
				name: '3000PN-CA 01-23',
				formClass: EnrollmentForm,
			},
			[DocumentType.IrrevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PN-IRASSIGN%2001-25%20%289_10_25%29.pdf',
				name: 'PN-IRASSIGN 01-23',
				supplemental: true,
				formClass: IrrevocableAssignmentForm,
			},
			[DocumentType.RevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PN-REASSIGN%2001-25%20%289_10_25%29.pdf',
				name: 'PN-REASSIGN 01-23',
				supplemental: true,
				formClass: RevocableAssignmentForm,
			},
			[DocumentType.SummaryOfCoverage]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000%20CA%20Summary%20of%20Coverage%2001-23%20%289_10_25%29.pdf',
				name: 'Summary-Of-Coverage-CA',
				supplemental: true,
				formClass: SummaryOfCoverageForm,
			},
		},
		CO: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-CO%2006-18%289.30.2025%29.pdf',
				name: '3000G&S-CO 06/18',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-I-CO%2001-20%20%2810.14.25%29.pdf',
				name: '3000PN(v9)-I-CO01-20',
				formClass: EnrollmentForm,
			},
			[DocumentType.CancellationPolicy]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN-NOC-CO%2004-15%289.18.2025%29.pdf',
				name: '3000PN-NOC-CO 04/15',
				supplemental: true,
				formClass: null,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/REPL-MODEL_2000%2012-21%20%2810.02.2025%29.pdf',
				name: 'REPL-MODEL_2000 12-21',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
		},
		CT: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-CT10-19%20%2810.9.25%29.pdf',
				name: '3000G&S-CT10-19',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-I-CT%2007-14%20%2810.7.25%29.pdf',
				name: '3000PN(v9)-I-CT 07-14',
				formClass: EnrollmentForm,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/REPL-MODEL_2000%2012-21%20%2810.02.2025%29.pdf',
				name: 'REPL-MODEL_2000 12-21',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
		},
		DC: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/G%26S/3000G&S-DC+06-18.pdf',
				name: '3000G&S-DC 06-18',
				formClass: GoodsAndServicesForm,
			},
		},
		FL: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-FL%2007-18%20%2810-1-25%29.pdf',
				name: '3000G&S-FL 07-18',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN-I-FL%2002-22%20%2810.3.25%29.pdf',
				name: '3000PN-I-FL 02-22',
				formClass: EnrollmentForm,
			},
			[DocumentType.RevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PN-REASSIGN-FL%2001-25%20%289_8_25%29.pdf',
				name: 'PN-REASSIGN-FL 01-23',
				supplemental: true,
				formClass: RevocableAssignmentForm,
			},
			[DocumentType.IrrevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PN-IRASSIGN-FL%2001-25%20%289_8_25%29.pdf',
				name: 'PN-IRASSIGN-FL 03-20',
				supplemental: true,
				formClass: IrrevocableAssignmentForm,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/R1-FL%2012-21%20%289_8_25%29.pdf',
				name: 'R1-FL 12-21',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
			[DocumentType.CremationAuthorization]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/Florida%20Cremation%20Authorization%20%289_9_25%29.pdf',
				name: 'FL Cremation Authorization',
				supplemental: true,
				formClass: CremationAuthorizationForm,
			},
		},
		GA: {
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-I-GA%2006-11%20%2810-3-25%29.pdf',
				name: '3000PN(v9)-GA 06-11',
				formClass: EnrollmentForm,
			},
			[DocumentType.IrrevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PN-IRASSIGN-GA%2001-25.pdf',
				name: 'PN-IRASSIGN-GA 12-21',
				formClass: IrrevocableAssignmentForm,
			},
			[DocumentType.RevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PN-REASSIGN%2001-25%20%289%2010%2025%29.pdf',
				name: 'PN-REASSIGN 01-25',
				formClass: RevocableAssignmentForm,
			},
		},
		HI: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/G%26S/3000G&S-HI+07-18.pdf',
				name: '3000G&S-HI 07-18',
				formClass: GoodsAndServicesForm,
			},
		},
		IA: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-IA%2011-23%289.30.2025%29.pdf',
				name: '3000G&S-IA 11-23',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-IA%2007-19%2810.3.2025%29.pdf',
				name: '3000PN(v9)-IA 07-19',
				formClass: EnrollmentForm,
			},
		},
		ID: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-ID%2007-18%2010.2.2025.pdf',
				name: '3000G&S-ID 07-18',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.SecondaryAddressee]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3333%2001-23%20%289%3A19%3A25%29.pdf',
				name: '3333 01-23',
				supplemental: true,
				formClass: SecondaryAddresseeForm,
			},
		},
		IL: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-IL%2007-18%289.30.2025%29v2.pdf',
				name: '3000G&S-IL& 07-18',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-IL-NGL%2004-14.pdf',
				name: '3000PN(v9)-IL-NGL 04-14',
				formClass: EnrollmentForm,
			},
			[DocumentType.SecondaryAddressee]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3333%2001-23%20%289%3A19%3A25%29.pdf',
				name: '3333 01-23',
				supplemental: true,
				formClass: SecondaryAddresseeForm,
			},
		},
		IN: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-IN%2007-18%20%289.19.25%29.pdf',
				name: '3000G&S-IN 07-18',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-IN%2010-16%20%2810.3.25%29.pdf',
				name: '3000PN(v9)-IN 10-16',
				formClass: EnrollmentForm,
			},
		},
		KS: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-KS%2007-18%20%2810.2.2025%29.pdf',
				name: '3000G&S-KS+07-18',
				formClass: GoodsAndServicesForm,
			},
		},
		KY: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-KY%2007-18%20%2810.2.2025%29.pdf',
				name: '3000G&S-KY 07-18',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-I-KY%2006-11%20%2810.10.2025%29.pdf',
				name: '3000PN(v9)-I-KY 06-11',
				formClass: EnrollmentForm,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/REPL-MODEL_2000%2012-21%20%2810.02.2025%29.pdf',
				name: 'REPL-MODEL_2000 12-21',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
		},
		LA: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-LA%2011-21%20%2810.6.25%29.pdf',
				name: '3000G&S-LA 11-21',
				formClass: GoodsAndServicesForm,
			},
		},
		MA: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000PN-FSM%2007-19%20%289.18.25%29.pdf',
				name: '3000G&S-MA 07-19',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-I-MA%2004-14%20%2810.3.25%29.pdf',
				name: '3000PN(v9)-I-MA 04-14',
				formClass: EnrollmentForm,
			},
			[DocumentType.PreneedAgreement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PN-REI-SGS-MA%2010-18%20%289_11_25%202.0%29%20.pdf',
				name: 'PN-REI-SGS-MA 10-18',
				formClass: PreneedAgreementForm,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/R1-MA%2012-21.pdf',
				name: 'R1-MA 12-21',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
		},
		MD: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-MD%2007-18%20%2810.10.2025%29.pdf',
				name: '3000G&S-MD 07-18',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-I-MD%2011-12%20%2810.10.2025%29v1.pdf',
				name: '3000PN(v9)-I-MD 11-12',
				formClass: EnrollmentForm,
			},
			[DocumentType.RevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PN-REASSIGN%2001-25%20%289_10_25%29.pdf',
				name: 'PN-REASSIGN 01-23',
				supplemental: true,
				formClass: RevocableAssignmentForm,
			},
			[DocumentType.IrrevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PN-IRASSIGN-MD%2001-25%20%2810.10.2025%29.pdf',
				name: 'PN-IRASSIGN-MD 01-25',
				supplemental: true,
				formClass: IrrevocableAssignmentForm,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/REPL-MODEL_2000%2012-21%20%2810.02.2025%29.pdf',
				name: 'REPL-MODEL_2000 12-21',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
			[DocumentType.GuaranteedPreneedDisclosure]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PNDISCL-G-MD%2012-21%20%2810.10.2025%29.pdf',
				name: 'PNDISCL-G-MD 12-21',
				supplemental: true,
				formClass: DisclosureForm,
			},
			[DocumentType.DeferredAnnuityDisclosure]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/1850-PN2%2011-21%2010.02.2025.pdf',
				name: '1850 PN2 11-21',
				supplemental: true,
				formClass: DisclosureForm,
			},
		},
		MI: {
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN-SG-MI%2010-23%20%2810.3.25%29.pdf',
				name: '3000PN-SG-MI 10-23',
				formClass: EnrollmentForm,
			},
			[DocumentType.RevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PN-REASSIGN-MI%2010-23.pdf',
				name: 'PN-REASSIGN-MI 10-23',
				formClass: RevocableAssignmentForm,
			},
			[DocumentType.IrrevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/NAC8-MI-NGL%2010-23.pdf',
				name: 'NAC8-MI-NGL 10-23',
				formClass: IrrevocableAssignmentForm,
			},
			[DocumentType.ReplacementInformation]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/R2-MI%2003-20.pdf',
				name: 'R2-MI 03-20',
				supplemental: true,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/R1-MI%2012-21.pdf',
				name: 'R1-MI 12-21',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
		},
		MN: {
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN-I-MN%2002-22%20%2810.14.25%29.pdf',
				name: '3000PN-I-MN 02-22',
				formClass: EnrollmentForm,
			},
			[DocumentType.IrrevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/Supplemental/PN-IRASSIGN%2001-25.pdf',
				name: 'PN-IRASSIGN 01-25',
				formClass: IrrevocableAssignmentForm,
			},
			[DocumentType.RevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/Supplemental/PN-REASSIGN%2001-25.pdf',
				name: 'PN-REASSIGN 01-25',
				formClass: RevocableAssignmentForm,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/R1-MN%2001-24.pdf',
				name: 'R1-MN 01-24',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
		},
		MO: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-MO%2012-21%289.30.2025%29.pdf',
				name: '3000G&S-MO 12-21',
				formClass: GoodsAndServicesForm,
			},
		},
		MS: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-MS%2006-20%20%289.16.25%29v3.pdf',
				name: '3000G&S-MS 06/20',
				formClass: GoodsAndServicesForm,
			},
		},
		MT: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-MT%2007-18%20%2810.13.2025%29.pdf',
				name: '3000G&S-MT 07-18',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-I-MT%2004-17%20%2810.13.2025%29.pdf',
				name: '3000PN-I-MT 04-17',
				formClass: EnrollmentForm,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/REPL-MODEL_2000%2012-21%20%2810.02.2025%29.pdf',
				name: 'REPL-MODEL_2000 12-21',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
			[DocumentType.DeferredAnnuityDisclosure]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PNDISCL-MT%2012-21%20%2810.13.2025%29.pdf',
				name: 'PNDISCL-MT 12/21',
				supplemental: true,
				formClass: DisclosureForm,
			},
		},
		NC: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/NCBFS%20ISG-S%2012-12%20%2810.17.2025%29.pdf',
				name: 'NCBFS ISG&S 10/03(rev. 12/12)',
				formClass: GoodsAndServicesForm,
				alternateFormClass: NGLNCGoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-I-NC%2006-11%20%2810.17.25%29.pdf',
				name: '3000PN(v9)-I-NC 06-11',
				formClass: EnrollmentForm,
				alternateFormClass: NGLNCEnrollmentForm,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/REPL-MODEL_2000%2012-21%20%2810.02.2025%29.pdf',
				name: 'REPL-MODEL_2000 12-21',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
			[DocumentType.CremationAuthorization]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/Form-59-Standard-Cremation-Authorization%20%2810.15.25%29.pdf',
				name: 'BFS-59',
				supplemental: true,
				formClass: CremationAuthorizationForm,
				alternateFormClass: NGLNCCremationAuthorizationForm,
			},
			[DocumentType.PreneedContractGuaranteed]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/NCBFS%20PNII%2003-04%20%2810.15.2025%29.pdf',
				name: 'NCBFS PNII 10/03 (rev. 03/04)',
				supplemental: true,
				formClass: PreneedContractForm,
				alternateFormClass: NGLNCPreneedContractForm,
			},
		},
		ND: {
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-ND%2006-11%20%2810-10-2025%29.pdf',
				name: '3000PN-ND 06-11',
				formClass: EnrollmentForm,
			},
			[DocumentType.IrrevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PN-IRASSIGN%2001-25%20%289%2010%2025%29.pdf',
				name: 'PN-IRASSIGN 01-25',
				supplemental: true,
				formClass: IrrevocableAssignmentForm,
			},
			[DocumentType.RevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PN-REASSIGN%2001-25%20%289%2010%2025%29.pdf',
				name: 'PN-REASSIGN 01-25',
				supplemental: true,
				formClass: RevocableAssignmentForm,
			},
		},
		NE: {
			[DocumentType.DeferredAnnuityDisclosure]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PNDISCL-NE%2005-24%2010.08.2025.pdf',
				name: 'PNDISCL-NE 05-24',
				supplemental: true,
				formClass: DisclosureForm,
			},
		},
		NH: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/G%26S/3000G&S-NH+07-18.pdf',
				name: '3000G&S-NH 07-18',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/Enrollment/3000PN(v9)-I-NH+06-11.pdf',
				name: '3000PN(v9)-I-NH 06-11',
				formClass: EnrollmentForm,
			},
			// TODO: File does not exist in s3
			// [DocumentType.PolicyReplacement]: {
			// 	link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/Supplemental/REPL-MODEL2000+03-20.pdf',
			// 	name: 'REPL-MODEL_2000 12-21',
			// },
		},
		NJ: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/G%26S/3000G&S-NJ+01-19.pdf',
				name: '3000G&S-NJ 01-19',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/Enrollment/2735PN-I-NJ(v2)Precoa)+09-15.pdf',
				name: '2735PN-I-NJ(v2)Precoa) 09-15',
				formClass: EnrollmentForm,
			},
			// TODO: File does not exist in s3
			// [DocumentType.PolicyReplacement]: {
			// "link": "https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/Supplemental/REPL-MODEL2000+03-20.pdf",
			// "name": "REPL-MODEL_2000 12-21"
			// }
		},
		NM: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-NM%2007-18%20%2810.2.2025%29.pdf',
				name: '3000G&S-NM 07-18',
				formClass: GoodsAndServicesForm,
			},
		},
		NV: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-NV%2010.9.2025.pdf',
				name: '3000G&S-NV 01-24',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/RA-NV%2012-21%2010.01.2025.pdf',
				name: 'RA-NV 03-20',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
			[DocumentType.CancellationPolicy]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN-NOC-NV%2010.01.2025.pdf',
				name: '3000PN-NOC-CO 04/15',
				supplemental: true,
				formClass: null,
			},
			[DocumentType.DeferredAnnuityDisclosure]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/1850-PN2%2011-21%2010.02.2025.pdf',
				name: 'PNDISCL-MT 12/21',
				supplemental: true,
				formClass: DisclosureForm,
			},
		},
		OH: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-OH%2011-21%20%2810.15.2025%29.pdf',
				name: '3000G&S-OH 11-21',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-I-OH%2006-11%20%2810.9.2025%29v1.pdf',
				name: '3000PN(v9)-I-OH 06-11',
				formClass: EnrollmentForm,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/REPL-MODEL_2000%2012-21%20%2810.02.2025%29.pdf',
				name: 'REPL-MODEL_2000 12-21',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
			[DocumentType.AuthorizedAgentDisclosure]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/Ohio%20Authorized%20Agent%20Disclosure%20Form.pdf',
				name: 'Authorized Agent Disclosure Form-OH 06-22',
				formClass: DisclosureForm,
			},
		},
		OK: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-OK%2007-18%20%289.19.25%29.pdf',
				name: '3000G&S-OK 07-18',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-I-OK%2006-11%20%2810.3.25%29.pdf',
				name: '3000PN(v9)-I-OK 06-11',
				formClass: EnrollmentForm,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/R1-OK%2012-21%20%289.17.25%29.pdf',
				name: 'R1-OK 12-21',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
			[DocumentType.ReplacementInformation]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/R2-OK%2012-21%20%289.17.25%29.pdf',
				name: 'R2-OK 12-21',
				supplemental: true,
				formClass: ReplacementInformationForm,
			},
		},
		OR: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-OR%2005-25%20%289.17.25%29.pdf',
				name: '3000G&S-OR 05-25',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN-I-OR%2002-22%20%2810.3.25%29.pdf',
				name: '3000PN-I-OR 02-22',
				formClass: EnrollmentForm,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/REPL-MODEL_2000%2003-20%20%289.10.25%29.pdf',
				name: 'REPL-Model 2000 03/20',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
		},
		RI: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-RI-Part%201%2007-18%2010.08.2025.pdf',
				name: '3000G&S-RI-Part 1 07-18',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.SecondaryAddressee]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3333%2001-23%20%289%3A19%3A25%29.pdf',
				name: '3333 01-23',
				supplemental: true,
				formClass: SecondaryAddresseeForm,
			},
			[DocumentType.DeferredAnnuityDisclosure]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/1850-PN2%2011-21%2010.02.2025.pdf',
				name: '1850-PN2 11-21',
				supplemental: true,
				formClass: DisclosureForm,
			},
		},
		SC: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000PN-FSM%2007-19%20%28SC%29.pdf',
				name: '3000PN-FSM 07/19',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v3%29%2002-22%20%289-29-25%29.pdf',
				name: '3000PN-SC 02-22',
				formClass: EnrollmentForm,
			},
			[DocumentType.PreneedContractGuaranteed]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PN-SGS-SC%2006-20%20%289-30-25%29.pdf',
				name: 'PN-SGS-SC 06-20',
				supplemental: true,
				formClass: PreneedContractForm,
			},
			[DocumentType.PreneedContractNonGuaranteed]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PN-NGSGS-SC%2006-20%20%289-30-25%29.pdf',
				name: 'PN-NGSGS-SC 06-20',
				supplemental: true,
				formClass: PreneedContractForm,
			},
		},
		SD: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/G%26S/3000G&S-SD+07-18.pdf',
				name: '3000G&S-SD 07-18',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/Enrollment/3000PN(v9)-I-SD+09-14.pdf',
				name: '3000PN(v9)-I-SD 09-14',
				formClass: EnrollmentForm,
			},
			[DocumentType.IrrevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/Supplemental/PN-IRASSIGN+01-23.pdf',
				name: 'PN-IRASSIGN 01-23',
				formClass: IrrevocableAssignmentForm,
			},
			// TODO: File does not exist in s3
			// [DocumentType.PolicyReplacement]: {
			// "link": "https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/Supplemental/REPL-MODEL2000+03-20.pdf",
			// "name": "REPL-MODEL_2000 12-21"
			// },
			[DocumentType.RevocableAssignment]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/Supplemental/PN-REASSIGN+01-23.pdf',
				name: 'PN-REASSIGN 01-23',
				formClass: RevocableAssignmentForm,
			},
		},
		TN: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S%28vC%29-TN%2007-26.pdf',
				name: '3000G&S-(vC) TN 07/26',
				formClass: TennesseeGoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-TN%2006-11%20%2810.3.25%29.pdf',
				name: '3000PN(v9)-TN 06-11',
				formClass: EnrollmentForm,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/R1-TN%2012-21%20%2809.19.2025%29.pdf',
				name: 'R1-TN 12/21',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
		},
		TX: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/PN-SGS-TX1-4%2010-24%2012.2.2025.pdf',
				name: 'PN-SGS-TX1 10/24',
				formClass: TexasGoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN-I-TX%2002-22%2012.5.2025.pdf',
				name: '3000PN-I-TX 02-22',
				formClass: EnrollmentForm,
			},
			[DocumentType.SummaryOfCoverage]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000%20TX%20Summary%20of%20Coverage%2011-21%2012.4.2025.pdf',
				name: '3000 TX Summary of Coverage 11/21',
				supplemental: true,
				formClass: SummaryOfCoverageForm,
			},
		},
		UT: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/PN-REI-SGS-UT%20-%20Fillable%2010.29.2025.pdf',
				name: 'PN-REI-SGS-UT 01/07',
				formClass: GoodsAndServicesForm,
				alternateFormClass: NGLUTGoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-I-UT%2006-11%2010.29.2025.pdf',
				name: '3000PN-I-UT 06-11',
				formClass: EnrollmentForm,
				alternateFormClass: NGLUTEnrollmentForm,
			},
			[DocumentType.DisclosureStatement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/PNDISCL-G-UT%2012-21%2010.29.2025.pdf',
				name: 'PNDISCL-G-UT 12/21',
				formClass: DisclosureForm,
				alternateFormClass: NGLUTDisclosureForm,
				supplemental: true,
			},
		},
		VT: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/G%26S/3000G&S-VT+07-18.pdf',
				name: '3000G&S-VT 07-18',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/Enrollment/3000PN(v9)-I-VT+06-11.pdf',
				name: '3000PN(v9)-I-VT 06-11',
				formClass: EnrollmentForm,
			},
			// TODO: File does not exist in s3
			// [DocumentType.PolicyReplacement]: {
			// "link": "https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/Supplemental/REPL-MODEL2000+03-20.pdf",
			// "name": "REPL-MODEL_2000 12-21"
			// }
		},
		VA: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-VA%2010-22%289.30.2025%29.pdf',
				name: '3000G&S-VA 10-22',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN-SG-VA%2003-22%2810.3..2025%29.pdf',
				name: '3000PN-SG-VA 03-22',
				formClass: EnrollmentForm,
			},
		},
		WA: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-WA%2012-21%20%2810.10.25%29.pdf',
				name: '3000G&S-WA 12-21',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN-WA%2006-23%20%2810.10.25%29v1.pdf',
				name: '3000PN-WA 06-23',
				formClass: EnrollmentForm,
			},
			[DocumentType.PolicyReplacement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/R1-WA%2012-21%20%2810.10.25%29.pdf',
				name: 'R1-WA 12-21',
				supplemental: true,
				formClass: PolicyReplacementForm,
			},
			[DocumentType.DisclosureStatement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/2756PN-WA%2010-24%20%2810.10.25%29.pdf',
				name: '1850 PN2 11-21',
				supplemental: true,
				formClass: DisclosureForm,
			},
		},
		WV: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/PN-REI-SGS-WV%2007-03%2012.4.2025v1.pdf',
				name: 'PN-REI-SGS-WV 07-03',
				formClass: NGLWVGoodsAndServicesForm,
			},
		},
		WI: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/G%26S/3000G%26S-WI%2010-20%2012.4.2025.pdf',
				name: '3000G&S-WI 10-20',
				formClass: NGLWIGoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28v9%29-WI%20%2006-11%2012.4.2025.pdf',
				name: '3000PN(v9)-WI 06-11',
				formClass: NGLWIEnrollmentForm,
			},
			[DocumentType.DisclosureStatement]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/1850-PN2%2011-21%2010.02.2025.pdf',
				name: '1850-PN2 11-21',
				supplemental: true,
				formClass: NGLWIDisclosureStatement,
			},
		},
		WY: {
			[DocumentType.StatementOfGoodsAndServices]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/G%26S/3000G&S-WY+07-18.pdf',
				name: '3000G&S-WY 07-18',
				formClass: GoodsAndServicesForm,
			},
			[DocumentType.EnrollmentForm]: {
				link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio/NGL/Enrollment/3000PN(v9)-WY++06-20.pdf',
				name: '3000PN(v9)-WY 06-20',
				formClass: EnrollmentForm,
			},
		},
	},
	[RoleType.funeralHomeCorporateAffiliation]: {
		[CorporateAffiliation.Carriage]: {
			CA: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-CA-NGL%2001-23.pdf',
					name: '3000PN-CA 01-23',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			CT: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-I-CT-NGL%20%2007-14%20%2810.7.25%29.pdf',
					name: '3000PN-I-CT 07-14',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			FL: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-I-FL-NGL%2002-22.pdf',
					name: '3000PN-I-FL 02-22',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			GA: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-GA-NGL%2006-11.pdf',
					name: '3000PN-GA 06-11',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			IL: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-IL-NGL%2008-12%289.19.2025%29.pdf',
					name: '3000PN-IL 08-12',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			OH: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-I-OH-NGL%2006-11%20%2810.3.2025%29.pdf',
					name: '3000PN-I-OH 06-11',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			DC: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-NGL%2006-11%20%2810-3-2025%29.pdf',
					name: '3000PN 06-11',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			ID: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-NGL%2006-11%20%2810-3-2025%29.pdf',
					name: '3000PN 06-11',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			KS: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-NGL%2006-11%20%2810-3-2025%29.pdf',
					name: '3000PN 06-11',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			KY: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-I-KY-NGL%20%2009.30.2025.pdf',
					name: '3000PN-I-KY 06-11',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			LA: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-NGL%2006-11%20%2810-3-2025%29.pdf',
					name: '3000PN 06-11',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			NV: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-NGL%2006-11%20%2810-3-2025%29.pdf',
					name: '3000PN 06-11',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			NM: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-NGL%2006-11%20%2810-3-2025%29.pdf',
					name: '3000PN 06-11',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			PA: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-NGL%2006-11%20%2810-3-2025%29.pdf',
					name: '3000PN 06-11',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			RI: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-NGL%2006-11%20%2810-3-2025%29.pdf',
					name: '3000PN 06-11',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			WI: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-WI-NGL%2006-11%2012.4.2025.pdf',
					name: '3000PN-WI 06-11',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			WA: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-WA%2006-23%20%289.30.25%29.pdf',
					name: '3000PN-WA 06-23',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			VA: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-SG-VA-NGL%2003-22%289.19.2025%29.pdf',
					name: '3000PN-SG-VA 03-22',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			TX: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-I-TX-NGL%2002-22%2012.5.2025.pdf',
					name: '3000PN-I-TX 02-22',
					formClass: EnrollmentForm,
				},
			},
			TN: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-TN-NGL%2006-11%20%289.18.25%29.pdf',
					name: '3000PN-TN 06-11',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			OK: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-I-OK-NGL%2006-11%20%289.17.25%29.pdf',
					name: '3000PN-I-OK 06-11',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			NC: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-I-NC-NGL%2006-11.pdf',
					name: '3000PN-I-NC 06-11',
					formClass: EnrollmentForm,
					alternateFormClass: NGLNCEnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			NJ: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/2735PN-I-NJ%28vC%2909-15%20%2810.2.25%29.pdf',
					name: '2735PN-I-NJ 09/15',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			MA: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-I-MA-NGL%2004-14%2001-08-26.pdf',
					name: '3000PN-I-MA 04-14',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			MT: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN%28vC%29-I-MT-NGL%2004-17%2010.03.2025.pdf',
					name: '3000PN-I-MT 04-17',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
			MI: {
				[DocumentType.EnrollmentForm]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Enrollment/3000PN-SG-MI%20%28vC%29%2010-23.pdf',
					name: '3000PN-SG-MI 10-23',
					formClass: EnrollmentForm,
				},
				[DocumentType.CancellationPolicy]: {
					link: 'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales%20Studio/NGL/Supplemental/3000PN%28vC%29-NOC%2001-23.pdf',
					name: '3000PN-NOC 01-23',
					formClass: null,
				},
			},
		},
	},
}

/**
 * Returns every document rule a corporate affiliation defines for a state,
 * e.g. "Carriage" + "OH" -> { [DocumentType.EnrollmentForm]: {...} }.
 * Returns undefined when the funeral home has no affiliation, or the
 * affiliation has no overrides for that state.
 */
export const getCorporateAffiliationRules = (
	stateCode: string,
	corporateAffiliation?: string
): DefaultDocumentRule | undefined => {
	if (!corporateAffiliation || !stateCode) {
		return undefined
	}

	return DocumentRules[RoleType.funeralHomeCorporateAffiliation][
		corporateAffiliation
	]?.[stateCode]
}

export const getDocumentRule = (
	stateCode: string,
	formType: DocumentType,
	corporateAffiliation?: string
) => {
	const corporateAffiliationDocumentRule = getCorporateAffiliationRules(
		stateCode,
		corporateAffiliation
	)?.[formType]

	const stateDocumentRule = stateCode
		? DocumentRules[RoleType.state][stateCode as ValidState]
		: undefined
	const defaultDocumentRule = DocumentRules[RoleType.default][formType]

	return (
		corporateAffiliationDocumentRule ||
		stateDocumentRule?.[formType] ||
		defaultDocumentRule
	)
}
